import { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, QueryClient, useMutation } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import debounce from 'lodash/debounce';

import {
  BodyWrapper,
  Header,
  Loader,
  MainWrapper,
  Message,
  PagesList,
} from '@components';
import { nonRenderingPageTypes } from '@constants';
import { usePageTemplates } from '@hooks';
import { pagesQuery, siteQuery, themeQuery, pageMutation } from '@queries';
import { SearchIcon } from '@svgs';

import { SiteBreadcrumbs } from '../components/SiteBreadcrumbs/SiteBreadcrumbs.jsx';
import SiteSidebar from '../components/SiteSidebar/SiteSidebar.jsx';
import { GlobalContext } from '@contexts';

const matchPage = (page, query) => {
  // determine filter match for a particular Page

  // explicit matching only
  const exactMatchFields = [page.id.toString()];
  if (exactMatchFields.some((v) => v && v.toLowerCase() === query)) {
    return true;
  }

  // string match for fields
  const pageFields = [page.name, page.path, page.title];
  if (pageFields.some((v) => v && v.toLowerCase().indexOf(query) > -1)) {
    return true;
  }

  return false;
};

const filterPages = (pages, query) => {
  // client side filtering of pages
  const cleanedQuery = query.toLowerCase().trim();
  return pages.filter((page) => {
    if (matchPage(page, cleanedQuery)) {
      return page;
    }
  });
};

export const Pages = () => {
  const { t, i18n } = useTranslation();
  const { siteID } = useParams();

  const globals = useContext(GlobalContext);

  const restrictPageTemplates = globals.group_settings?.restrict_page_templates;

  // filter search term
  const [searchTerm, setSearchTerm] = useState('');

  // get site
  const { data: siteData, isPending: siteIsPending } = useQuery(
    siteQuery(siteID),
  );

  // get pages
  //
  // gcTime set to 0 to always force a re-query in case pages have
  // been updated in other frames (i.e. site builder), etc.
  const {
    data: pagesData,
    isPending: pagesIsPending,
    refetch: refetchPages,
  } = useQuery(
    pagesQuery(
      {
        order_by: 'pattern',
        site_id: siteID,
        _fields: [
          'id',
          'site_id',
          'name',
          'path',
          'type',
          'is_draft',
          'active',
          'noindex',
          'custom_field_published',
          'full_path',
          'custom_page_type',
          'template_name',
        ].join(','),
      },
      { gcTime: 0 },
    ),
  );

  // get theme
  const { data: themeData, isPending: themeIsPending } = useQuery(
    themeQuery(siteData?.theme_id),
  );

  // Site ID of the parent site via theme data
  const parentSiteId = themeData?.site_override?.en;

  // get parent pages
  const {
    data: parentPagesData,
    isPending: parentPagesIsPending,
    refetch,
  } = useQuery(
    pagesQuery(
      {
        active: 1, // published
        order_by: 'pattern',
        site_id: parentSiteId,
        _fields: [
          'id',
          'site_id',
          'name',
          'path',
          'type',
          'is_draft',
          'active',
          'noindex',
          'custom_field_published',
          'full_path',
          'custom_page_type',
        ].join(','),
      },
      {
        enabled: !!parentSiteId,
      },
    ),
  );

  const { data: pageTemplates } = usePageTemplates(
    siteID,
    siteData?.theme_id,
    null,
    ['template_name', 'name'],
  );

  const handleFilterChange = (e) => {
    setSearchTerm(e.target.value);
  };

  // https://medium.com/nerd-for-tech/debounce-your-search-react-input-optimization-fd270a8042b
  const debouncedResults = useMemo(() => {
    return debounce(handleFilterChange, 300);
  }, []);

  useEffect(() => {
    return () => {
      debouncedResults.cancel();
    };
  });

  // filter pages to only renderable page types
  const renderingPages = pagesData
    ? pagesData.objects.filter(
        (page) => !nonRenderingPageTypes.includes(page.type),
      )
    : [];

  // filter pages to only renderable page types
  const renderingParentPages = parentPagesData
    ? parentPagesData.objects.filter(
        (page) => !nonRenderingPageTypes.includes(page.type),
      )
    : [];

  // filter websites if there is a search term, otherwise return the full list
  const filteredPages = searchTerm
    ? filterPages(renderingPages, searchTerm)
    : renderingPages;

  const queryClient = new QueryClient();

  // creating a duplicate of a selecteed page.
  const {
    isPending: isMutatePending,
    mutate,
    isError: isMutateError,
  } = useMutation(
    pageMutation(null, {
      onSuccess: () => {
        // invalidate list views so they are re-fetched
        queryClient.invalidateQueries({ queryKey: ['pages', 'list'] });
        refetchPages();
      },
    }),
  );

  const duplicatPage = (pageID) => {
    //find the selected page from id
    const selectedPage = pagesData.objects.find((p) => p.id == pageID);
    //prepping the payload
    const payload = {
      clone_id: pageID,
      site_id: siteID,
      name: selectedPage.name + ' (Duplicate)',
    };

    mutate(payload, {
      onError: (error) => {
        console.log('error -', error);
      },
    });
  };
  return (
    <>
      <Header />
      <BodyWrapper>
        <SiteSidebar siteID={siteID} siteData={siteData} />
        <MainWrapper>
          <SiteBreadcrumbs siteData={siteData}>
            <li className="sb-flex">
              <span>{t('Pages')}</span>
            </li>
          </SiteBreadcrumbs>
          <div className="sb-manage-content">
            <div className="sb-manage-content-head">
              <h1>{t('Pages')}</h1>
              <div className="sb-button-list-inline">
                <Link
                  to={`/dashboard/sites/${siteID}/pages/new`}
                  className="sb-btn sb-btn-cancel"
                >
                  Add new
                </Link>
              </div>
            </div>
            <div className="sb-manage-options-menu sb-flex">
              <div className="sb-manage-options-left">
                <div className="sb-flex">
                  <form
                    role="search"
                    className="sb-manage-options-search"
                    onSubmit={(e) => e.preventDefault()}
                  >
                    <input
                      type="search"
                      name="query"
                      placeholder="Filter"
                      aria-label="Enter search term"
                      autoComplete="off"
                      defaultValue={searchTerm}
                      onChange={debouncedResults}
                      disabled={pagesIsPending}
                    />
                    <SearchIcon />
                  </form>
                </div>
              </div>
              <div className="sb-manage-options-right"></div>
            </div>
            {(pagesIsPending || siteIsPending) && (
              <Loader className="sb-loader sb-loader-large" />
            )}
            {pagesData && siteData && (
              <>
                {isMutatePending ? (
                  <Loader className="sb-loader sb-loader-block" />
                ) : (
                  <PagesList
                    pages={filteredPages}
                    site={siteData}
                    pageTemplates={pageTemplates}
                    duplicate={duplicatPage} // pass this to enable duplication of pages.
                  />
                )}
                {!pagesIsPending && searchTerm && (
                  <Message
                    messageStyle="notice"
                    title={
                      `Filtered to ${filteredPages.length} of ${renderingPages.length} page` +
                      (renderingPages.length != 1 ? 's' : '')
                    }
                    fadeoutMessage={false}
                    isFormMessage={false}
                  />
                )}
                {themeIsPending && (
                  <Loader className="sb-loader sb-loader-block" />
                )}
                {isMutateError && (
                  <Message
                    messageStyle="error"
                    title="Error duplicating page"
                    message="Please try duplicating page again."
                  />
                )}
                {parentSiteId && (
                  <>
                    <div style={{ paddingTop: '60px', paddingBottom: '25px' }}>
                      <h2>Inherited pages</h2>
                      <p>
                        These pages are inherited from the{' '}
                        {restrictPageTemplates?.length > 0 ? (
                          'current website template'
                        ) : (
                          <Link
                            to={`/dashboard/projects/${siteData.business_id}/templates/${parentSiteId}/pages`}
                          >
                            current website template
                          </Link>
                        )}
                        .
                      </p>
                    </div>
                    {parentPagesIsPending && (
                      <Loader className="sb-loader sb-loader-block" />
                    )}
                    {parentPagesData && (
                      <PagesList
                        readonly={true}
                        pages={renderingParentPages}
                        site={siteData}
                        pageTemplates={pageTemplates}
                      />
                    )}
                  </>
                )}
              </>
            )}
          </div>
        </MainWrapper>
      </BodyWrapper>
    </>
  );
};
